import type { MessageDto } from '@restaurant/shared';
import { format } from 'date-fns';
import { AlertCircle, Check, CheckCheck, Clock, FileText, MapPin, Mic } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MediaImage } from './MediaImage';

function StatusTick({ status }: { status: MessageDto['status'] }) {
  switch (status) {
    case 'queued':
      return <Clock className="size-3.5" aria-label="Queued" />;
    case 'sent':
      return <Check className="size-3.5" aria-label="Sent" />;
    case 'delivered':
      return <CheckCheck className="size-3.5" aria-label="Delivered" />;
    case 'read':
      return <CheckCheck className="size-3.5 text-sky-500" aria-label="Read" />;
    case 'failed':
      return <AlertCircle className="size-3.5 text-destructive" aria-label="Failed" />;
    default:
      return null;
  }
}

export function MessageBubble({ m, quoted }: { m: MessageDto; quoted?: MessageDto }) {
  const out = m.direction === 'outbound';
  const payload = m.payload as Record<string, Record<string, unknown> | undefined>;
  const failedReason = m.status === 'failed' ? ((m.error as { message?: string; code?: string } | null)?.message ?? 'Failed to send') : null;

  if (m.type === 'reaction') {
    return (
      <div className={cn('flex', out ? 'justify-end' : 'justify-start')}>
        <span className="text-xl" title="Reaction">{m.text}</span>
      </div>
    );
  }

  let body: React.ReactNode = m.text ? <p className="whitespace-pre-wrap break-words">{m.text}</p> : null;
  if (m.media && (m.type === 'image' || m.type === 'sticker')) {
    body = (
      <div className="space-y-1">
        <MediaImage mediaId={m.media.id} alt={m.text ?? 'Image'} />
        {m.text ? <p className="whitespace-pre-wrap break-words">{m.text}</p> : null}
      </div>
    );
  } else if (m.type === 'image' && typeof payload.image?.link === 'string') {
    // Outbound picture sent by link (our upload or a menu photo).
    body = (
      <div className="space-y-1">
        <img src={payload.image.link} alt={m.text ?? 'Image'} className="max-h-72 rounded-lg object-contain" />
        {m.text ? <p className="whitespace-pre-wrap break-words">{m.text}</p> : null}
      </div>
    );
  } else if (m.type === 'location') {
    const loc = payload.location as { latitude?: number; longitude?: number; name?: string; address?: string } | undefined;
    body = (
      <a className="flex items-start gap-2 underline-offset-2 hover:underline" target="_blank" rel="noreferrer" href={`https://maps.google.com/?q=${loc?.latitude},${loc?.longitude}`}>
        <MapPin className="mt-0.5 size-4 shrink-0" />
        <span>{loc?.name ?? 'Location'}{loc?.address ? <span className="block text-xs opacity-80">{loc.address}</span> : null}</span>
      </a>
    );
  } else if (m.type === 'audio') {
    body = <p className="flex items-center gap-2 italic opacity-80"><Mic className="size-4" /> Voice message</p>;
  } else if (m.type === 'document' && typeof payload.document?.link === 'string') {
    // Outbound document sent by link (e.g. a receipt PDF).
    const filename = typeof payload.document.filename === 'string' ? payload.document.filename : 'Document';
    body = (
      <div className="space-y-1">
        <a href={payload.document.link} target="_blank" rel="noreferrer" className="flex items-center gap-2 font-medium underline-offset-2 hover:underline">
          <FileText className="size-4 shrink-0" /> {filename}
        </a>
        {m.text && m.text !== filename ? <p className="whitespace-pre-wrap break-words text-xs opacity-80">{m.text}</p> : null}
      </div>
    );
  } else if (m.type === 'document') {
    body = <p className="flex items-center gap-2"><FileText className="size-4" /> {m.text ?? 'Document'}</p>;
  } else if (m.type === 'template') {
    body = <p className="whitespace-pre-wrap break-words"><span className="mr-1 rounded bg-black/5 px-1 text-[10px] uppercase tracking-wide dark:bg-white/10">Template</span>{m.text?.replace(/^\[template\] /, '')}</p>;
  } else if (!body) {
    body = <p className="italic opacity-70">[{m.type}]</p>;
  }

  return (
    <div className={cn('flex', out ? 'justify-end' : 'justify-start')}>
      <div className={cn('max-w-[78%] rounded-xl px-3 py-1.5 text-sm shadow-xs', out ? 'rounded-tr-sm bg-bubble-out' : 'rounded-tl-sm bg-bubble-in')}>
        {quoted ? (
          <div className="mb-1 border-l-2 border-primary/60 bg-black/5 px-2 py-1 text-xs opacity-80 dark:bg-white/5">
            {quoted.text ?? `[${quoted.type}]`}
          </div>
        ) : null}
        {body}
        <div className="mt-0.5 flex items-center justify-end gap-1 text-[10px] text-muted-foreground">
          {out && m.sentBy ? <span>{m.sentBy.name} ·</span> : null}
          {out && m.sentByAi ? <span className="font-medium text-primary" title="Sent automatically by the AI auto-reply">AI auto-reply ·</span> : null}
          <span>{format(new Date(m.timestamp), 'HH:mm')}</span>
          {out ? <StatusTick status={m.status} /> : null}
        </div>
        {failedReason ? <p className="mt-1 text-xs text-destructive">{failedReason}</p> : null}
      </div>
    </div>
  );
}
